import React, { useState } from "react"
import { FormattedMessage } from "react-intl"
import Bookmark from "../../bookmark/bookmark"
import { navigate } from "../../../link"
import ReactPlayer from "react-player"
import { WingsButton } from "@wingspans-ui"
import "./StoryCard.css"

/**
 * @typedef {Object} StoryCardContent - props used to pass the content of a story card
 * @property {string} key - unique string associated with this card
 * @property {string} photoURL - URL of image to use as background of card
 * @property {string} title - most prominent text on card
 * @property {string} subtitle1 - if included, displayed as smaller text above title
 * @property {string} subtitle2 - if included, displayed as smallest text below title
 * @property {boolean} isStudent - whether the card is a student story
 * @property {string} link - if included, the title will be a link to this URL
 * @property {string} quote - quote to display on back side of card
 * @property {string} storyText - full story on back side of card
 * @property {string} highlightedPhrase - if included, the first instance of this phrase in the quote will be highlighted
 */

/**
 * Extract info from Cosmic story object and convert it into an object compatible
 * with the StoryCard component
 * @param {Object} story
 * @returns {StoryCardContent}
 */
export const cosmicEmployeeStoryToStoryCardContent = (story) => {
  return {
    key: story.slug,
    photoURL: story.metadata.story_personal_photo?.imgix_url,
    title: story.title,
    subtitle2: story.metadata.story_job_title,
    quote: "",
    isStudent: false,
    link: `/story/${story.slug}`,
    bookmark: (
      <Bookmark
        contentTitle={story.title}
        classContext={"story-carousel-card"}
        contentId={`${story.slug}-${story.locale}`}
        contentSlug={story.slug}
        contentType={story.type}
      />
    ),
    videoURL: story.metadata.story_video_url,
  }
}

/**
 * Extract info from Cosmic story object and convert it into an object compatible
 * with the StoryCard component
 * @param {Object} story
 * @returns {StoryCardContent}
 */
export const cosmicStoryToStoryCardContent = (story) => {
  return {
    key: story.slug,
    photoURL: story.metadata.story_personal_photo?.imgix_url,
    videoURL: story.metadata.story_video_url,
    title: story.metadata.story_job_title,
    subtitle1: story.title,
    subtitle2: story.metadata.story_location,
    quote: "",
    isStudent: false,
    link: `/story/${story.slug}`,
    isAlumni: true,
  }
}

/**
 * Extract info from a Cosmic Student Story object and convert it into an object
 * compatible with the StoryCard component
 * @param {Object} studentStory - Cosmic Student Story object
 * @returns {StoryCardContent}
 */
export const cosmicStudentStoryToStoryCardContent = (studentStory) => {
  let subtitle2Components = []
  if (studentStory.metadata.degree_level) {
    subtitle2Components.push(studentStory.metadata.degree_level)
  }
  if (studentStory.metadata.degree_area) {
    subtitle2Components.push(studentStory.metadata.degree_area)
  }
  return {
    key: studentStory.slug,
    photoURL: studentStory.metadata.photo?.imgix_url,
    title: studentStory.title,
    subtitle2: subtitle2Components.join("\n"),
    isStudent: true,
    quote: studentStory.metadata.quote,
    storyText: studentStory.metadata.story,
    highlightedPhrase: studentStory.metadata.highlighted_phrase,
  }
}

// TODO: check if algolia changes will be merged because they both touch this component

const StoryCard = ({
  photoURL,
  title,
  subtitle1,
  subtitle2,
  isStudent = false,
  link = "",
  quote,
  storyText,
  highlightedPhrase,
  isPreview,
  isAlumni = false,
  bookmark = null,
  videoURL,
}) => {
  const [flipped, setFlipped] = useState(false)

  const cardIsFlippable = isStudent && !isPreview && (quote || storyText) && !videoURL

  let quoteBeginning = quote
  let quoteEnd = ""
  if (highlightedPhrase && quote) {
    const lowerCasePhrase = highlightedPhrase.toLowerCase()
    const lowerCaseQuote = quote.toLowerCase()
    const indexOfHighlightedPhrase = lowerCaseQuote.indexOf(lowerCasePhrase)
    quoteBeginning = quote.slice(0, indexOfHighlightedPhrase)
    quoteEnd = quote.slice(indexOfHighlightedPhrase + highlightedPhrase.length)
  }

  const [videoPlaying, setVideoPlaying] = useState(false)

  const handleClick = () => {
    if (cardIsFlippable) {
      setFlipped(!flipped)
    } else if (link) {
      navigate(link)
    }
  }

  return (
    <div className={`story-card-flip-container ${flipped ? "hover" : ""}`}>
      {!isPreview && bookmark}
      <div 
        className={`story-card-flipper ${isPreview ? "story-card-preview" : ""}`}
        onClick={handleClick}
      >
        <div
          className="story-card-card story-card-front "
          style={{
            '--story-card-overlay-bg': `linear-gradient(0.11deg, rgba(54, 54, 54, 0.78) 22.38%, rgba(54, 54, 54, 0.2) 35.65%, rgba(54, 54, 54, 0.28) 82.93%, rgba(54, 54, 54, 0.75) 93.74%), url(${photoURL})`,
          }}
        >
          {isAlumni && (
            <div className="story-card-meet-the-alumni">
              <FormattedMessage id="prospective-meet-the-alumni" defaultMessage="meet the alumni" />
            </div>
          )}
          <div className="story-card-subtitle1">{subtitle1}</div>
          {isStudent ? (
            <div className="story-card-title">
              {title}
            </div>
          ) : (
            <a className="story-card-title" href={link} onClick={(e) => e.stopPropagation()}>
              {title}
            </a>
          )}
          <div className="story-card-subtitle2">{subtitle2}</div>
          <div className="story-card-inner-border" />
        </div>
        <div
          className="story-card-card story-card-back"
          style={{
            '--story-card-overlay-bg': `linear-gradient(rgba(54, 54, 54, 0.9), rgba(54, 54, 54, 0.9)), url(${photoURL})`,
          }}
        >
          <div className="story-card-inner-border" />
          {quote && (
            <>
              <blockquote className="story-card-quote">
                "{quoteBeginning}
                {highlightedPhrase && (
                  <mark className="story-card-highlighted-phrase">
                    {highlightedPhrase}
                  </mark>
                )}
                {quoteEnd}"
              </blockquote>
              <cite className="story-card-quote-source">
                &mdash; {title}
              </cite>
            </>
          )}
          <p className="story-card-story-text">
            {storyText}
          </p>
        </div>
      </div>
      {(videoURL && !isPreview) && (videoPlaying ? (
        <div className="story-card-video-container">
          <ReactPlayer
            url={videoURL}
            controls={true}
            playing={videoPlaying}
            width="100%"
            height="100%"
          />
        </div>
      ) : (
        <WingsButton
          className="story-card-video-play-button"
          size="tiny"
          variant="tal-e"
          iconName="video"
          onClick={() => setVideoPlaying(true)}
        >
          <FormattedMessage id="wings-card-watch-video" defaultMessage="Watch Video" />
        </WingsButton>
      ))}
    </div>
  )
}

export default StoryCard
